import { api } from "../lib";
import type { DocumentViewResponse } from "../types/reportTemplate";
import {
  ReportContentChangedError,
  type IssuedReportReview,
  type ReportContentVersion,
  type ReportPreviewData,
  type ReportPreviewEvidence,
} from "./signOff.service";

export interface AddendumMacro {
  id: string;
  name: string;
  shortcut: string | null;
  contentHtml: string;
  macroType: "addendum" | "amendment";
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AddendumCodeOption {
  id: string;
  code: string;
  description: string;
}

export interface AddendumDraft {
  id: string;
  caseId: string;
  name: string | null;
  contentHtml: string;
  sourceMacroId: string | null;
  additionalCptCodeIds: string[];
  additionalCptCodeQuantities: Record<string, number>;
  additionalIcdCodeIds: string[];
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddendumDraftInput {
  contentHtml: string;
  sourceMacroId: string | null;
  additionalCptCodeIds: string[];
  additionalCptCodeQuantities?: Record<string, number>;
  additionalIcdCodeIds: string[];
  revision?: number;
}

export interface AddendumOptions {
  macros: AddendumMacro[];
  cptCodes: AddendumCodeOption[];
  icdCodes: AddendumCodeOption[];
}

export interface AddendumSignOutResult {
  success: true;
  signOffId: string;
  reportVersion: number;
  deliveryQueue: unknown[];
  deliveryQueueFailed: boolean;
  pdfGenerationQueueFailed: boolean;
  sessionId: string;
  sessionExpiresAt?: string;
}

export interface AddendumSignOutPreview {
  previewData: ReportPreviewData;
  documentViewData: DocumentViewResponse;
  baseDocumentViewData?: DocumentViewResponse;
  review: IssuedReportReview;
  templateId: string | null;
  draftRevision: number;
  baseReportVersion: number;
}

export interface AddendumEligibility {
  canCreateFromExternalBaseline: boolean;
  baselineId?: string;
  sendOutId?: string;
  reportVersion?: number;
  receivedAt?: string;
  downloadUrl?: string;
}

export interface FrontOfficeTissueCypherAddendumEligibility {
  canInitiate: boolean;
}

export type AddendumDraftAccess = "ordinary" | "front-office-tissue-cypher";

interface AddendumReviewHttpError {
  response?: {
    status?: number;
    data?: { code?: string };
  };
}

export const addendumService = {
  getEligibility: (caseId: string) =>
    api.get<AddendumEligibility>(`/cases/${caseId}/addendum-eligibility`),
  getFrontOfficeTissueCypherEligibility: (caseId: string) =>
    api.get<FrontOfficeTissueCypherAddendumEligibility>(
      `/cases/${caseId}/front-office-tissue-cypher-addendum-eligibility`
    ),
  getDraft: (caseId: string, access: AddendumDraftAccess = "ordinary") =>
    api.get<AddendumDraft | null>(
      access === "front-office-tissue-cypher"
        ? `/cases/${caseId}/front-office-tissue-cypher-addendum-draft`
        : `/cases/${caseId}/addendum-draft`
    ),
  getOptions: (caseId: string, access: AddendumDraftAccess = "ordinary") =>
    api.get<AddendumOptions>(
      access === "front-office-tissue-cypher"
        ? `/cases/${caseId}/front-office-tissue-cypher-addendum-options`
        : `/cases/${caseId}/addendum-options`
    ),
  createDraft: (
    caseId: string,
    input: AddendumDraftInput,
    access: AddendumDraftAccess = "ordinary"
  ) =>
    api.post<AddendumDraft>(
      access === "front-office-tissue-cypher"
        ? `/cases/${caseId}/front-office-tissue-cypher-addendum-drafts`
        : `/cases/${caseId}/addendum-drafts`,
      input
    ),
  updateDraft: (
    caseId: string,
    draftId: string,
    input: AddendumDraftInput,
    access: AddendumDraftAccess = "ordinary"
  ) =>
    api.patch<AddendumDraft>(
      access === "front-office-tissue-cypher"
        ? `/cases/${caseId}/front-office-tissue-cypher-addendum-drafts/${draftId}`
        : `/cases/${caseId}/addendum-drafts/${draftId}`,
      input
    ),
  deleteDraft: (caseId: string, draftId: string) =>
    api.delete<void>(`/cases/${caseId}/addendum-drafts/${draftId}`),
  getSignOutPreview: (caseId: string, draftId: string, revision: number) =>
    api.get<AddendumSignOutPreview>(
      `/cases/${caseId}/addendum-drafts/${draftId}/sign-off/preview?revision=${revision}`
    ),
  acknowledgeSignOutPreview: async (
    caseId: string,
    draftId: string,
    revision: number,
    contentVersion: ReportContentVersion
  ): Promise<ReportPreviewEvidence> => {
    try {
      return await api.post<ReportPreviewEvidence>(
        `/cases/${caseId}/addendum-drafts/${draftId}/sign-off/preview-review`,
        { revision, contentVersion }
      );
    } catch (error) {
      const response = (error as AddendumReviewHttpError).response;
      if (
        response?.status === 409 &&
        response.data?.code === "REPORT_CONTENT_CHANGED"
      ) {
        throw new ReportContentChangedError();
      }
      throw error;
    }
  },
  signOutDraft: (
    caseId: string,
    draftId: string,
    input: {
      revision: number;
      sessionId?: string | null;
      password?: string;
      previewEvidence?: ReportPreviewEvidence;
      reviewedPanelKeys?: string[];
    }
  ) =>
    api.post<AddendumSignOutResult>(
      `/cases/${caseId}/addendum-drafts/${draftId}/sign-out`,
      input
    ),
};
